import { useCallback, useEffect, useMemo, useState } from 'react';

import { Divider, Modal, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';

import { productKeys, useGetCodesMenuMenuCodeQuery, useGetSkusQuery } from '~/hooks/product';
import { transformToSelectOptions } from '~/utils';

import { INITIAL_PRODUCT_PAGE_OFFSET, INITIAL_PRODUCT_PAGE_SIZE } from '~/constants/product';

import ClaimExchangeItemAddFilter from './ClaimExchangeItemAddFilter';
import ClaimExchangeItemAddTable from './ClaimExchangeItemAddTable';

import type {
  TypeExchangeItemAddData,
  TypeExchangeItemAddFilterValues,
  TypeExchangeItemAddFilterValuesState,
} from '~/types/claim.types';
import type { TypeGetSkusParams, TypeProductOperatingPageCodesMenu } from '~/types/product.types';

const DEFAULT_SEARCH_FILTER_VALUES: TypeExchangeItemAddFilterValues = {
  keywordType: null,
  keyword: '',
  pagination: {
    currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
    pageSize: INITIAL_PRODUCT_PAGE_SIZE,
    totalCount: 0,
  },
};

const DEFAULT_SEARCH_FILTER_VALUES_STATE: TypeExchangeItemAddFilterValuesState = {
  keyword: 'default',
};

const ClaimExchangeItemAddModal = ({
  isOpen,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (selectedItems: TypeExchangeItemAddData[]) => void;
}) => {
  const queryClient = useQueryClient();

  const codesMenuQuery = useGetCodesMenuMenuCodeQuery<TypeProductOperatingPageCodesMenu>({
    menuCode: 'PROD_SKU',
    showGlobalDimOverlay: false,
  });

  const searchFilterOptions = useMemo(() => {
    if (!codesMenuQuery.data) return { keywordType: [] };
    const keywordOptions = transformToSelectOptions(codesMenuQuery.data.skuKeyword.codeItemList, 'code', 'name');
    return { keywordType: keywordOptions.slice(0, 2) };
  }, [codesMenuQuery.data]);

  const [searchFilterValues, setSearchFilterValues] = useState<TypeExchangeItemAddFilterValues>(() => ({
    ...DEFAULT_SEARCH_FILTER_VALUES,
    keywordType: searchFilterOptions.keywordType[0] ?? null,
  }));
  const [searchFilterValuesState, setSearchFilterValuesState] = useState<TypeExchangeItemAddFilterValuesState>(
    DEFAULT_SEARCH_FILTER_VALUES_STATE
  );

  const [listQueryParams, setListQueryParams] = useState<TypeGetSkusParams | null>(null);
  const skusQuery = useGetSkusQuery({
    params: listQueryParams ?? ({} as TypeGetSkusParams),
    enabled: !!listQueryParams,
    showGlobalDimOverlay: false,
  });

  const [selectedItems, setSelectedItems] = useState<TypeExchangeItemAddData[] | null>(null);

  useEffect(() => {
    if (!codesMenuQuery.data) return;

    setSearchFilterValues((prev) => ({
      ...prev,
      keywordType: prev.keywordType ?? searchFilterOptions.keywordType[0] ?? null,
    }));
  }, [codesMenuQuery.data, searchFilterOptions.keywordType]);

  useEffect(() => {
    if (!skusQuery.data) return;
    setSelectedItems(null);
    setSearchFilterValues((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, totalCount: skusQuery.data.pagination.listTotal },
    }));
  }, [skusQuery.data]);

  const handleChangeFilterValues = (values: Partial<TypeExchangeItemAddFilterValues>) => {
    setSearchFilterValues({ ...searchFilterValues, ...values });
  };

  const handleChangeFilterValuesState = (valuesState: Partial<TypeExchangeItemAddFilterValuesState>) => {
    setSearchFilterValuesState({ ...searchFilterValuesState, ...valuesState });
  };

  const handleSearch = useCallback(
    (filterValues: TypeExchangeItemAddFilterValues) => {
      const params: TypeGetSkusParams = {
        keyWord: filterValues.keywordType?.value ?? '',
        keyWordValue: filterValues.keyword,
        status: 'ACTIVE',
        isActive: true,
        limit: filterValues.pagination.pageSize,
        cursor: filterValues.pagination.currentPage,
      };

      setSearchFilterValues(filterValues);
      queryClient.invalidateQueries({ queryKey: productKeys.skus(params) });
      setListQueryParams(params);
    },
    [queryClient]
  );

  const handleReset = () => {
    setSearchFilterValues((prev) => ({
      ...prev,
      keywordType: searchFilterOptions.keywordType[0] ?? null,
      keyword: '',
    }));
    setSearchFilterValuesState(DEFAULT_SEARCH_FILTER_VALUES_STATE);
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_PRODUCT_PAGE_OFFSET, pageSize: value.value },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({ ...searchFilterValues, pagination: { ...searchFilterValues.pagination, currentPage: value } });
  };

  const handleChangeRowSelection = (selectedRows: TypeExchangeItemAddData[]) => {
    setSelectedItems(selectedRows);
  };

  const handleConfirm = () => {
    if (!selectedItems) return;
    onConfirm(selectedItems);
  };

  return (
    <Modal
      title="교환 상품 추가"
      size="large"
      confirmText="추가"
      disabled={!selectedItems?.length}
      open={isOpen}
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <ClaimExchangeItemAddFilter
        values={searchFilterValues}
        valuesState={searchFilterValuesState}
        options={searchFilterOptions}
        onChange={handleChangeFilterValues}
        onChangeState={handleChangeFilterValuesState}
        onSearch={() =>
          handleSearch({
            ...searchFilterValues,
            pagination: {
              ...searchFilterValues.pagination,
              currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
            },
          })
        }
        onReset={handleReset}
      />
      <Divider color="gray100" />
      <ClaimExchangeItemAddTable
        data={(skusQuery.data?.data as TypeExchangeItemAddData[]) ?? null}
        pagination={searchFilterValues.pagination}
        onChangeRowSelection={handleChangeRowSelection}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
      />
    </Modal>
  );
};

export default ClaimExchangeItemAddModal;
